<template>
  <StaffLayout section="Respond Search" showHeader>
    <Loading v-if="isLoading" />
    <div v-else>
      <div class="pb-8 border-b">
        <div class="my-4">
          <!-- Use a form to handle "enter" keystroke -->
          <form v-on:submit.prevent>
            <input
              v-model.lazy="searchText"
              placeholder="Search"
              class="w-full md:w-3/4 lg:w-1/2 shadow appearance-none border rounded py-1 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            />
            <SearchButton
              @click="searchLibrary"
              class="block ml-0 mt-2 md:mt-0 md:inline md:ml-2"
              type="submit"
            >
              Search
            </SearchButton>
          </form>
        </div>
        <div class="lg:w-1/2 2xl:w-1/3">
          <BaseSelect class="mt-2" :options="searchTypeOptions" v-model="searchType" />
        </div>
        <div class="lg:w-1/2 2xl:w-1/3">
          <div v-if="searchType == RespondSearchType.SearchAll" class="mt-2">
            <BaseCheckbox label="Include Overrides" v-model="includeOverrides" />
          </div>
          <BaseSelect
            v-else-if="searchType == RespondSearchType.SearchLibrary"
            class="mt-2"
            label="Response Library"
            :options="libraryOptions"
            v-model="selectedLibId"
          />
          <BaseSelect
            v-else-if="searchType == RespondSearchType.SearchPortal"
            class="mt-2"
            label="Portal"
            :options="portalOptions"
            v-model="selectedPortalId"
          />
        </div>
      </div>
      <Loading v-if="isSearchLoading" />
      <div v-else>
        <div class="mt-4" v-if="respondStore.searchText">
          Showing
          {{
            (respondStore.searchResults?.libItems?.length ?? 0) +
            (respondStore.searchResults?.overrides?.length ?? 0)
          }}
          results for "{{ respondStore.searchText }}"<span
            v-if="respondStore.searchType == RespondSearchType.SearchAll"
            >.
          </span>
          <span v-else-if="respondStore.searchType == RespondSearchType.SearchLibrary">
            in {{ getResponseLibNameById(respondStore.searchLibraryId) }}.
          </span>
          <span v-else-if="respondStore.searchType == RespondSearchType.SearchPortal">
            in {{ getPortalNameById(respondStore.searchPortalId) }}.
          </span>
        </div>
        <div v-if="libItemResults?.length > 0 || overrideResults?.length > 0">
          <div v-if="libItemResults?.length > 0">
            <div
              v-if="respondStore.searchType != RespondSearchType.SearchLibrary"
              class="text-3xl font-bold pt-8"
            >
              Library Items ({{ libItemResults.length }})
            </div>
            <div v-for="item in libItemResults" class="pt-4">
              <div
                v-if="respondStore.searchType != RespondSearchType.SearchLibrary"
                class="font-bold"
              >
                {{ getResponseLibNameById(item.responseLibId) }}
              </div>
              <div>
                {{ item.responseTypeName }} / {{ getRoleTypeById(item.roleTypeId).name }}
              </div>
              <div class="border my-2 p-2 response-steps text-gray-500">
                <span v-html="item.responseText"></span>
              </div>
            </div>
          </div>
          <div v-if="overrideResults?.length > 0">
            <div
              v-if="respondStore.searchType != RespondSearchType.SearchLibrary"
              class="text-3xl font-bold pt-8"
            >
              Overrides ({{ overrideResults.length }})
            </div>
            <div v-for="item in overrideResults" class="pt-4">
              <div
                v-if="respondStore.searchType != RespondSearchType.SearchPortal"
                class="font-bold"
              >
                {{ getPortalNameById(item.portalId) }}
              </div>
              <div>
                {{ item.responseTypeName }} / {{ item.siteRoleName }}
                <span v-if="item.isHidden" class="">(hidden)</span>
              </div>
              <div class="border my-2 p-2 response-steps text-gray-500">
                <span v-html="item.responseText"></span>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="pt-4">
          <ErrorDisplay :is-visible="requestError.length > 0" :message="requestError" />
        </div>
      </div>
    </div>
  </StaffLayout>
</template>

<script setup lang="ts">
import { NameValue } from "../../sharedTypes";
import { useAdminStore } from "../../store/adminStore";
import { useBldgStore } from "../../store/bldgStore";
import { useRespondStore, RespondSearchType } from "../../store/respondStore";

const bldgStore = useBldgStore();
const respondStore = useRespondStore();
const adminStore = useAdminStore();
const isLoading = ref(false);

const libItemResults = computed(() => respondStore.searchResults?.libItems);
const overrideResults = computed(() => respondStore.searchResults?.overrides);

onMounted(loadData);
async function loadData() {
  isLoading.value = true;

  await bldgStore.getPropertyList();
  await respondStore.getLibraryList();
  await adminStore.getRoleTypes();

  searchType.value = respondStore.searchType;
  searchText.value = respondStore.searchText;
  selectedLibId.value = respondStore.searchLibraryId;
  selectedPortalId.value = respondStore.searchPortalId;
  includeOverrides.value = respondStore.includeOverrides;

  isLoading.value = false;
}

//-----------------------------------
// #region Search options 🔎
//-----------------------------------

const searchTypeOptions = [
  { name: "Search All Response Content", value: RespondSearchType.SearchAll },
  { name: "Search by Library", value: RespondSearchType.SearchLibrary },
  { name: "Search by Portal", value: RespondSearchType.SearchPortal },
];
const libraryOptions = computed(() =>
  respondStore.libraryList.map(
    (l) =>
      ({
        name: l.name,
        value: l.responseLibId,
      } as NameValue)
  )
);
let portalOptions = computed(() =>
  bldgStore.properties.map((p) => ({ name: p.name, value: p.portalId } as NameValue))
);

const isSearchLoading = ref(false);
const searchText = ref("");
const searchType = ref(RespondSearchType.SearchAll);
const includeOverrides = ref(false);
const selectedPortalId = ref(0);
const selectedLibId = ref(0);
const requestError = ref("");

async function searchLibrary() {
  isSearchLoading.value = true;
  requestError.value = "";

  if (searchText.value.length > 0) {
    switch (Number(searchType.value)) {
      case RespondSearchType.SearchAll:
        requestError.value = await respondStore.searchAllResponseContent(
          searchText.value,
          includeOverrides.value
        );
        break;

      case RespondSearchType.SearchLibrary:
        if (selectedLibId.value > 0)
          requestError.value = await respondStore.searchResponseContentByLibrary(
            searchText.value,
            selectedLibId.value
          );
        break;

      case RespondSearchType.SearchPortal:
        if (selectedPortalId.value > 0)
          requestError.value = await respondStore.searchResponseContentByPortal(
            searchText.value,
            selectedPortalId.value
          );
        break;
    }
  }

  isSearchLoading.value = false;
}

//-----------------------------------
// #endregion Search options 🔎
//-----------------------------------

// Helpers
var getRoleTypeById = (roleTypeId: number) =>
  adminStore.roleTypes.find((t) => t.roleTypeId == roleTypeId);

var getResponseLibNameById = (responseLibId: number) =>
  respondStore.libraryList.find((t) => t.responseLibId == responseLibId)?.name;

var getPortalNameById = (portalId: number) =>
  bldgStore.properties.find((p) => p.portalId == portalId)?.name;
</script>

<style scoped>
.response-steps {
  font-size: medium;
}

.response-steps :deep(a) {
  color: blue;
  text-decoration: underline;
  cursor: pointer;
}

.response-steps :deep(a:hover) {
  color: cornflowerblue;
}

.response-steps :deep(ul) {
  padding-left: 24px;
}

.response-steps :deep(ul > li) {
  font-weight: normal;
  list-style-type: circle;
}

.response-steps :deep(ol) {
  counter-reset: li;
  margin-left: 0;
  padding-left: 0;
}

.response-steps :deep(ol > li) {
  position: relative;
  margin: 0 0 2px 2em;
  padding: 0 8px;
  list-style: none;
}

.response-steps :deep(ol > li:before) {
  content: counter(li);
  counter-increment: li;
  position: absolute;
  top: 0px;
  left: -1em;
  box-sizing: border-box;
}

.response-steps :deep(h2) {
  font-size: x-large;
}

.response-steps :deep(h3) {
  font-size: larger;
}

.response-steps :deep(h4) {
  font-size: large;
}
</style>
